import { uploadBytesResumable } from "firebase/storage";

const DEFAULT_STALL_MS = 25000;
const DEFAULT_FIRST_PROGRESS_STALL_MS = 12000;
const DEFAULT_MAX_RETRIES = 2;
const ONLINE_WAIT_MS = 20000;

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function toUploadError(err, code, message) {
  const nextError = err instanceof Error ? err : new Error(message);
  nextError.code = code;
  return nextError;
}

function isRetryableStorageError(err) {
  const code = String(err?.code || "");
  return (
    code === "storage/retry-limit-exceeded" ||
    code === "storage/canceled" ||
    code === "storage/unknown" ||
    code === "storage/internal-error" ||
    code === "storage/network-request-failed"
  );
}

async function waitForOnline(maxWaitMs = ONLINE_WAIT_MS) {
  if (typeof navigator === "undefined" || navigator.onLine) return;

  await new Promise((resolve) => {
    let done = false;
    let timeoutId = null;

    const finish = () => {
      if (done) return;
      done = true;
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener("online", onOnline);
      resolve();
    };

    const onOnline = () => {
      finish();
    };

    window.addEventListener("online", onOnline);
    timeoutId = setTimeout(finish, maxWaitMs);
  });
}

async function uploadAttempt({
  storageRef,
  file,
  metadata,
  stallMs,
  firstProgressStallMs,
  onTask,
  onProgress,
}) {
  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, file, metadata);
    if (typeof onTask === "function") onTask(task);

    let didSettle = false;
    let stallTimer = null;
    let sawOffline = false;
    let hasSeenProgress = false;

    const clearStallTimer = () => {
      if (stallTimer) {
        clearTimeout(stallTimer);
        stallTimer = null;
      }
    };

    const resetStallTimer = () => {
      clearStallTimer();
      const nextStallMs = hasSeenProgress ? stallMs : firstProgressStallMs;
      stallTimer = setTimeout(() => {
        try {
          task.cancel();
        } catch {
          // ignore cancel failures
        }
        cleanup();
        reject(
          toUploadError(
            new Error("Upload stalled before progress resumed."),
            "storage/network-request-failed",
            "Upload stalled before progress resumed."
          )
        );
      }, nextStallMs);
    };

    const cleanup = () => {
      if (didSettle) return;
      didSettle = true;
      clearStallTimer();
      window.removeEventListener("offline", onOffline);
    };

    const onOffline = () => {
      sawOffline = true;
      try {
        task.cancel();
      } catch {
        // ignore cancel failures
      }
    };

    window.addEventListener("offline", onOffline);
    resetStallTimer();

    task.on(
      "state_changed",
      (snapshot) => {
        hasSeenProgress = true;
        resetStallTimer();
        if (typeof onProgress === "function") onProgress(snapshot);
      },
      (err) => {
        cleanup();
        if (sawOffline) {
          reject(
            toUploadError(
              err,
              "storage/network-request-failed",
              "Network connection lost during upload."
            )
          );
          return;
        }
        reject(err);
      },
      () => {
        cleanup();
        resolve();
      }
    );
  });
}

export async function uploadWithRetries({
  storageRef,
  file,
  metadata,
  maxRetries = DEFAULT_MAX_RETRIES,
  stallMs = DEFAULT_STALL_MS,
  firstProgressStallMs = DEFAULT_FIRST_PROGRESS_STALL_MS,
  onTask,
  onProgress,
}) {
  const attempts = Math.max(1, Number(maxRetries) + 1);
  let lastError = null;

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      if (attempt > 1) {
        await waitForOnline();
      }

      await uploadAttempt({
        storageRef,
        file,
        metadata,
        stallMs,
        firstProgressStallMs,
        onTask,
        onProgress,
      });
      return;
    } catch (err) {
      lastError = err;
      const shouldRetry = attempt < attempts && isRetryableStorageError(err);
      if (!shouldRetry) break;
      const baseDelayMs = 600 * Math.pow(2, attempt - 1);
      const jitterMs = Math.floor(Math.random() * 250);
      await sleep(baseDelayMs + jitterMs);
    }
  }

  throw lastError || new Error("Upload failed.");
}
